<script setup>
import { computed, ref } from 'vue';
import api from 'dashboard/api/aceleres/integrations';
const props = defineProps({
  state: { type: Object, required: true },
  manager: Boolean,
});
const emit = defineEmits(['close', 'refresh', 'connect', 'manage']);
const pending = computed(() =>
  props.manager ? props.state.contract_request : props.state.mine.pending
);
const initial = (() => {
  if (!pending.value) return 1;
  if (props.manager) return pending.value.target_total - props.state.contracted;
  return pending.value.quantity;
})();
const quantity = ref(Math.max(1, initial));
const reason = ref(pending.value?.reason || '');
const busy = ref(false);
const error = ref('');
const unused = computed(() =>
  (props.state.people || []).filter(
    p => p.limit > p.used && Number(p.id) !== Number(props.state.mine.user_id)
  )
);
const date = computed(() =>
  pending.value
    ? new Date(pending.value.created_at).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : ''
);
const title = computed(() => {
  if (!pending.value) return 'Sem chave disponível';
  if (!props.manager && pending.value.stage === 'zoraxy')
    return 'Pedido aguardando a equipe Zoraxy';
  return props.manager
    ? 'Você já tem uma solicitação em aberto'
    : 'Pedido aguardando o gestor';
});
const submit = async () => {
  busy.value = true;
  error.value = '';
  try {
    if (props.manager) {
      emit(
        'refresh',
        await api.requestKeyContract(props.state.contracted + quantity.value)
      );
    } else {
      await api.requestKeys(quantity.value, reason.value);
      emit('refresh', await api.getKeyEntitlements());
    }
    emit('close');
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
const cancel = async () => {
  busy.value = true;
  try {
    emit(
      'refresh',
      props.manager
        ? await api.cancelKeyContract()
        : await api.cancelKeyRequest(pending.value.id)
    );
    emit('close');
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
const reuse = async person => {
  busy.value = true;
  try {
    emit('refresh', await api.useUnusedKey(person.id));
    emit('connect');
    emit('close');
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[110] flex items-center justify-center bg-modal-backdrop-dark p-4"
      @click.self="!busy && emit('close')"
    >
      <section
        class="w-full max-w-lg rounded-[18px] bg-n-solid-1 p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="key-request-title"
      >
        <h2 id="key-request-title" class="text-lg font-semibold">
          {{ title }}
        </h2>
        <p v-if="pending" class="mt-2 text-[13px] text-n-slate-11">
          Pedido de +{{ initial }}
          {{ initial === 1 ? 'chave' : 'chaves' }} enviado em {{ date }} ·
          {{
            manager || pending.stage === 'zoraxy'
              ? 'aguardando a equipe Zoraxy'
              : 'aguardando o gestor'
          }}. A resposta chega nas notificações.
        </p>
        <p v-else-if="manager" class="mt-2 text-[13px] text-n-slate-11">
          As {{ state.contracted }} chaves da empresa estão distribuídas.
          Escolha como seguir:
        </p>
        <p v-else class="mt-2 text-[13px] text-n-slate-11">
          Você está usando todas as suas chaves ({{ state.mine.used }} de
          {{ state.mine.limit }}). Vamos enviar uma solicitação ao seu gestor.
        </p>
        <div
          v-if="manager && !pending && unused.length"
          class="mt-5 rounded-xl border-2 border-[#15803D] bg-[#F3FAF5] p-4"
        >
          <p class="text-xs font-semibold tracking-wide text-[#15803D]">
            CHAVES SEM USO
          </p>
          <div
            v-for="(person, index) in unused"
            :key="person.id"
            class="mt-3 flex items-center justify-between gap-3 text-[13px]"
          >
            <span
              >{{ person.name }} tem {{ person.limit - person.used }}
              {{
                person.limit - person.used === 1
                  ? 'chave sem uso'
                  : 'chaves sem uso'
              }}.</span
            >
            <button
              :disabled="busy"
              class="shrink-0 rounded-lg border border-[#15803D] px-3 py-2 text-xs"
              :class="
                index === 0 ? 'bg-[#16A34A] text-white' : 'text-[#15803D]'
              "
              @click="reuse(person)"
            >
              Usar esta chave
            </button>
          </div>
        </div>
        <button
          v-if="manager && !pending"
          class="mt-4 flex w-full items-center justify-between rounded-xl border border-n-weak p-4 text-left"
          @click="
            emit('manage');
            emit('close');
          "
        >
          <span
            ><strong class="text-sm">Ajustar em Gerenciar chaves</strong
            ><small class="mt-1 block text-n-slate-11"
              >Chaves em uso não podem ser retiradas.</small
            ></span
          ><span>›</span>
        </button>
        <div
          class="mt-4 flex items-center justify-between gap-3 rounded-xl bg-n-alpha-2 p-4"
        >
          <div>
            <strong class="text-sm">{{
              pending
                ? 'Quantidade solicitada'
                : manager
                  ? 'Solicitar nova chave'
                  : 'Quantas chaves?'
            }}</strong
            ><small
              v-if="manager"
              class="mt-1 block text-[13px] leading-5 text-n-slate-11"
              >Vai para a equipe Zoraxy liberar.</small
            >
          </div>
          <div class="flex items-center gap-3">
            <button
              :disabled="busy || quantity <= 1"
              class="rounded-lg border border-n-weak px-3 py-2 disabled:opacity-40"
              @click="quantity--"
            >
              −</button
            ><strong>{{ quantity }}</strong
            ><button
              :disabled="busy || quantity >= 1000000"
              class="rounded-lg border border-n-weak px-3 py-2"
              @click="quantity++"
            >
              +
            </button>
          </div>
        </div>
        <p
          v-if="pending && quantity !== initial"
          class="mt-2 text-[13px] text-[#9A4B00]"
        >
          era {{ initial }} · você está
          {{ quantity > initial ? 'adicionando mais' : 'reduzindo' }}
          {{ Math.abs(quantity - initial) }}
        </p>
        <template v-if="!manager"
          ><label class="mt-4 block text-sm"
            >Motivo (opcional)<textarea
              v-model="reason"
              maxlength="2000"
              rows="2"
              class="mt-2 w-full rounded-lg border border-n-weak bg-n-solid-1 p-3"
            /></label
        ></template>
        <p v-if="error" role="alert" class="mt-3 text-sm text-n-ruby-11">
          {{ error }}
        </p>
        <footer class="mt-6 flex flex-wrap items-center justify-end gap-2">
          <button
            v-if="pending"
            :disabled="busy"
            class="mr-auto h-10 text-sm text-[#B42318]"
            @click="cancel"
          >
            {{ manager ? 'Cancelar solicitação' : 'Cancelar pedido' }}
          </button>
          <button
            class="h-10 rounded-lg border border-n-weak px-4 text-sm"
            :disabled="busy"
            @click="emit('close')"
          >
            {{ pending ? 'Fechar' : 'Cancelar' }}
          </button>
          <button
            :disabled="busy"
            class="h-10 rounded-lg bg-[#16A34A] px-4 text-sm font-semibold text-white disabled:opacity-40"
            @click="submit"
          >
            {{
              pending
                ? 'Atualizar pedido'
                : manager
                  ? 'Enviar solicitação'
                  : 'Enviar ao gestor'
            }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
